import React, { useState, useEffect, useCallback } from 'react';
import { satisfaction as api } from '../services/api';

/**
 * Rapport de simulation d'un formulaire : qui recevrait quoi, et pourquoi les
 * autres sont écartés.
 *
 * Cet écran existe pour qu'on puisse le LIRE avant de passer un formulaire en
 * mode « actif ». Sans lui, la simulation ne vivrait que dans les journaux du
 * serveur — donc nulle part, en pratique.
 *
 * ⚠️ Il doit montrer le message du CANAL de chaque ligne. Jusqu'au 2026-09-17
 * la colonne affichait toujours le SMS, y compris pour les destinataires qui
 * auraient reçu un e-mail : on relisait un texte qui ne serait jamais parti.
 */

const ETATS = {
  simule: { label: 'Partirait', classe: 'status-success' },
  envoye: { label: 'Envoyé', classe: 'status-success' },
  ecarte: { label: 'Écarté', classe: 'status-warning' },
  echec: { label: 'Échec', classe: 'status-error' },
};

const parSms = (l) => l.canal === 'sms' || l.canal === 'sms+email';
const parEmail = (l) => l.canal === 'email' || l.canal === 'sms+email';

// Aperçu d'une ligne : ce que CE destinataire recevrait, pas un message type.
function apercu(l) {
  if (!l.canal) return null;
  if (parSms(l) && !parEmail(l)) return l.message;
  if (parEmail(l) && !parSms(l)) return l.email_objet;
  return `${l.email_objet} · + SMS`;
}

function DetailMessages({ ligne }) {
  return (
    <div className="sat-sim-detail">
      {parSms(ligne) && (
        <div>
          <b>SMS</b> <span className="sat-meta">vers {ligne.telephone} · {ligne.sms_longueur}/160 caractères</span>
          <div className="sat-message">{ligne.message || '—'}</div>
        </div>
      )}
      {parEmail(ligne) && (
        <div>
          <b>E-mail</b> <span className="sat-meta">vers {ligne.email}</span>
          <div className="sat-message"><b>{ligne.email_objet}</b></div>
          <div className="sat-message">{ligne.email_corps || '—'}</div>
        </div>
      )}
      {!ligne.canal && <div className="sat-meta">Aucun message : ce destinataire est écarté.</div>}
    </div>
  );
}

export default function SatisfactionSimulation({ formulaire, onClose }) {
  const [bilan, setBilan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');
  const [ouverte, setOuverte] = useState(null);

  const lancer = useCallback(async () => {
    setLoading(true);
    setErreur('');
    setOuverte(null);
    try {
      const { data } = await api.simuler(formulaire.id);
      setBilan(data);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Simulation impossible');
    } finally {
      setLoading(false);
    }
  }, [formulaire.id]);

  useEffect(() => { lancer(); }, [lancer]);

  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🧪 Simulation — {formulaire.nom}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p className="sat-aide">
            Rien ne part : cette page calcule exactement ce qui serait envoyé aujourd'hui.
            Cliquez une ligne pour lire le message entier tel qu'il partirait.
          </p>

          {loading && <div className="sat-vide">Calcul en cours…</div>}
          {erreur && <div className="status-error sat-bandeau">{erreur}</div>}

          {bilan && !loading && (
            <>
              {bilan.blocage && (
                <div className="status-warning sat-bandeau">
                  Aucun envoi possible : {bilan.blocage}.
                </div>
              )}
              {(bilan.avertissements || []).map((a, i) => (
                <div key={i} className="status-warning sat-bandeau">⚠️ {a}</div>
              ))}
              <div className="sat-kpis">
                <div className="sat-kpi"><span>{bilan.total}</span>Candidats</div>
                <div className="sat-kpi"><span>{bilan.simules}</span>Partiraient</div>
                <div className="sat-kpi"><span>{bilan.ecartes}</span>Écartés</div>
              </div>

              <div className="cr-scroll sat-sim-scroll">
                <table className="sat-sim-tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 26 }}></th>
                      <th>Client</th>
                      <th>Point de vente</th>
                      <th>Canal</th>
                      <th>Destinataire</th>
                      <th>Message qui partirait</th>
                      <th>Motif d'écart</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bilan.lignes.map((l, i) => {
                      const etat = ETATS[l.etat] || ETATS.ecarte;
                      const depliable = Boolean(l.canal);
                      const ouvert = ouverte === i;
                      return (
                        <React.Fragment key={i}>
                          <tr
                            className={`${l.etat === 'ecarte' ? 'sat-ligne-ecartee' : ''}${depliable ? ' sat-ligne-cliquable' : ''}`}
                            onClick={depliable ? () => setOuverte(ouvert ? null : i) : undefined}
                          >
                            <td className="sat-chevron">{depliable ? (ouvert ? '▾' : '▸') : ''}</td>
                            <td>
                              <span className={etat.classe}>{etat.label}</span>{' '}
                              {[l.prenom, l.nom].filter(Boolean).join(' ') || '—'}
                            </td>
                            <td>{l.site_nom || l.site_code || '—'}</td>
                            <td>{l.canal || '—'}</td>
                            <td>{l.telephone || l.email || '—'}</td>
                            <td className="sat-message sat-apercu-msg">{apercu(l) || '—'}</td>
                            <td>{l.motif || '—'}</td>
                          </tr>
                          {ouvert && (
                            <tr className="sat-sim-detail-ligne">
                              <td colSpan={7}><DetailMessages ligne={l} /></td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {bilan.lignes.length === 0 && (
                      <tr><td colSpan={7} className="sat-vide">Aucun candidat aujourd'hui.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={lancer} disabled={loading}>↻ Relancer</button>
          <button className="btn-primary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
